import packageJson from '../../../package.json';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  firstValueFrom,
  fromEvent,
  map,
  merge,
  Observable,
  shareReplay,
  startWith,
  Subject,
} from 'rxjs'; // ✅ Used for UI updates
import {
  UserAgent,
  Registerer,
  Inviter,
  Invitation,
  SessionState,
  Session,
  RegistererState,
  Subscriber,
  Notification,
  InviterOptions,
  UserAgentOptions,
} from 'sip.js';

import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';
import { CallDirection, EveCall } from '../models/eve-call';
import { Sip } from '../models/sip';
import { environment } from '../../environments/environment';

import { Base64 } from 'js-base64';
import { decryptDES, encryptDES } from '../../app/helpers/crypto-utils';
import { LogModule, LogService } from './log.service';
import { Status } from '../../generated/eveportal-api/graphql';
import { UserPresenceState } from '../enums/user-presence-state.enum';

export function isSessionOnHold(session: Session): boolean {
  const sdh = session.sessionDescriptionHandler as SessionDescriptionHandler;
  if (!sdh || !sdh.peerConnection) return false;

  const receivers = sdh.peerConnection.getReceivers();
  const audioTracks = receivers
    .map((r) => r.track)
    .filter((track) => track?.kind === 'audio');

  return audioTracks.length === 0;
}

const registererOptions = {
  expires: 300, // in seconds (e.g., 1 minutes)
};

export interface SipRegistrationDelegate {
  onRegistered?: () => void;
  onUnregistered?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class SipService {
  public sip: Sip = {} as any;

  private userAgent: UserAgent | undefined;
  private registerer: Registerer | undefined;

  // BehaviorSubject to maintain active calls.
  // Only calls with a session state other than Terminated will be shown in the UI.
  private activeCallsSubject = new BehaviorSubject<EveCall[]>([]);
  public activeCalls$ = this.activeCallsSubject.asObservable();

  // Centralized transfer state - shared across all contact cards for efficiency
  private transferableCall: EveCall | null = null;
  public canTransfer$ = this.activeCalls$.pipe(
    map((calls) => {
      // Find and cache the transferable call
      this.transferableCall =
        calls.find(
          (call) =>
            call.session?.state === SessionState.Established &&
            !call.isOnHold &&
            !call.isInConference
        ) || null;

      return !!this.transferableCall;
    }),
    shareReplay(1) // Cache the result to avoid recalculation
  );

  // Incoming calls (e.g., incoming and still in establishing state)
  private incomingCallsSubject = new BehaviorSubject<EveCall[]>([]);
  public incomingCalls$ = this.incomingCallsSubject.asObservable();

  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];

  private remoteStreamSubject = new BehaviorSubject<MediaStream | null>(null);
  public remoteStream$ = this.remoteStreamSubject.asObservable();

  private selectedMicrophone = new BehaviorSubject<string | null>(null);
  private selectedSpeaker = new BehaviorSubject<string | null>(null);
  public selectedSpeakerId$ = this.selectedSpeaker.asObservable();

  private remoteAudioElement: HTMLAudioElement | null = null; // Store audio element reference

  private activeStream: MediaStream | null = null; // Store active audio stream

  // Store the audio element for ringtone
  private ringtoneAudio: HTMLAudioElement;

  // Store the audio element for ringback (outgoing)
  private ringbackAudio: HTMLAudioElement;

  private userHasInteracted = false;

  public sipRegistrationState$ = new BehaviorSubject<RegistererState | null>(
    null
  );

  private dialogInfoXmlSubject = new BehaviorSubject<string | null>(null);

  public dialogInfoXml$ = this.dialogInfoXmlSubject.asObservable();

  public delegate?: SipRegistrationDelegate;

  public effectiveSipStatus$ = combineLatest([
    this.sipRegistrationState$,
    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(startWith(navigator.onLine)), // initial value
  ]).pipe(
    debounceTime(200), // Add a debounce time to avoid rapid state changes
    map(([sipState, isOnline]) => {
      if (!isOnline) return 'offline';
      switch (sipState) {
        case RegistererState.Registered:
          return 'registered';
        case RegistererState.Unregistered:
          return 'disconnected';
        case RegistererState.Terminated:
          return 'terminated';
        default:
          return 'connecting';
      }
    })
  );

  private selectedMicId: string | null = null;
  private selectedSpeakerId: string | null = null;

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isReconnectingSubject = new BehaviorSubject<boolean>(false);
  public isReconnecting$ = this.isReconnectingSubject.asObservable();

  private callTerminatedSubject = new Subject<EveCall>();
  public callTerminated$ = this.callTerminatedSubject.asObservable();

  private sessionStateListeners = new Map<string, () => void>();
  setAudioElement(audioElement: HTMLAudioElement) {
    this.remoteAudioElement = audioElement;
    console.log('🎧 Remote audio element set:', audioElement);
  }

  private presenceSubscriber: Subscriber | null = null;
  private userPresenceSubject = new BehaviorSubject<UserPresenceState>(
    UserPresenceState.OutOfOffice
  );
  public userPresence$ = this.userPresenceSubject.asObservable();

  constructor(private logService: LogService) {
    this.ringtoneAudio = new Audio('assets/ringtone.wav');
    this.ringtoneAudio.loop = true;

    // Outgoing ringback tone
    this.ringbackAudio = new Audio('assets/ringtonback.wav');
    this.ringbackAudio.loop = true;

    // Detect user interaction (for autoplay permissions)
    window.addEventListener(
      'click',
      () => {
        this.userHasInteracted = true;
      },
      { once: true }
    ); // ✅ fire only once

    window.addEventListener('online', async () => {
      console.log('📡 Network is back online — checking SIP status...');
      await this.autoReconnectIfNeeded();
    });
  }

  async initialize(loginRefreshToken: any) {
    if (!loginRefreshToken) {
      this.logService.logError(
        `❌ Refresh failed: Invalid loginRefreshToken`,
        LogModule.SIP
      );
      throw new Error('❌ Refresh failed');
    }

    // Clear any existing calls from previous sessions
    this.clearAllCalls();
    this.reconnectAttempts = 0;
    this.isReconnectingSubject.next(false);

    const encodeKey = environment.config.sip.encodeKey;
    const originalKey = Base64.decode(encodeKey);
    const clientKey = encryptDES(loginRefreshToken.salt, originalKey);

    const sipUser = decryptDES(loginRefreshToken.sipUserName, clientKey);
    const sipPassword = decryptDES(loginRefreshToken.sipPassword, clientKey);

    const sip = new Sip();
    const eveUser = loginRefreshToken?.eveUser;
    sip.server = environment.config.sip.server;
    sip.displayName =
      eveUser?.eveUserName ?? `${eveUser?.firstName} ${eveUser?.surname}`;
    sip.publicIdentity = `sip:${sipUser}`;
    sip.privateIdentity = sipUser.split('@')[0];
    sip.password = sipPassword; //discuss with chris
    sip.realm = sipUser.split('@')[1];
    sip.routes = environment.config.sip.routes;
    sip.iceUrls = environment.config.sip.iceUrls;

    this.sip = sip;
    try {
      console.log('✅ Running inside Microsoft Teams');
      await this.initializeSIP();
    } catch (error: any) {
      await this.logService.logError(
        `❌ Initialization Failed: ${error.message}`,
        LogModule.SIP
      );
      throw new Error(`❌ Initialization Failed: ${error.message}`);
    }
  }

  async initializeSIP() {
    try {
      console.log('🔄 Initializing SIP.js...');

      if (!navigator.onLine) {
        console.warn('❌ Browser is offline — SIP will not connect.');
        this.sipRegistrationState$.next(RegistererState.Terminated);
        throw new Error('❌ Browser is offline — SIP will not connect.');
      }

      const userAgentOptions: Partial<UserAgentOptions> = {
        uri: UserAgent.makeURI(this.sip.publicIdentity),
        authorizationUsername: this.sip.privateIdentity,
        authorizationPassword: this.sip.password,
        displayName: this.sip.displayName,
        userAgentString: 'EveUcTeamsClient-' + packageJson.version,
        preloadedRouteSet: this.sip.routes,
        transportOptions: {
          server: this.sip.server,
        },
        sessionDescriptionHandlerFactoryOptions: {
          iceGatheringTimeout: 500, //currently, the smallest allowed value
          peerConnectionConfiguration: {
            iceServers:
              this.sip.iceUrls == null
                ? null
                : this.sip.iceUrls.map((s) => ({
                    urls: s,
                  })),
            //   'iceTransportPolicy': "all"
          },
        },
        // ✅ Logging config at root level — not in transportOptions
        logBuiltinEnabled: false,
        logConfiguration: true, // optional
        logLevel: 'debug', // for maximum logging
        logConnector: async (level, category, label, content) => {
          console.log(`[LOG][${level}][${category}]`, content);
          if (category === 'sip.Transport') {
            await this.logService.logInfo(`${content}\n`, LogModule.SIP);
          }
        },
      };

      if (environment.current !== 'dev') {
        userAgentOptions.hackViaTcp = true; // Todo Enable VIA TCP hack if needed
      }

      // ✅ Check if userAgent already exists and is connected
      // if (this.userAgent) {
      //   console.log(
      //     '⚠️ UserAgent already exists, stopping existing instance...'
      //   );
      //   await this.userAgent.stop();
      //   this.userAgent = undefined;
      //   this.registerer = undefined;
      // }

      this.userAgent = new UserAgent(userAgentOptions);

      this.userAgent.delegate = {
        onConnect: () => {
          console.log('🟢 Transport connected');
        },
        onDisconnect: (error?: Error) => {
          this.sipRegistrationState$.next(RegistererState.Terminated);
          this.logService.logError(
            `❌ Transport disconnected: ${error?.message}`,
            LogModule.SIP
          );
          throw new Error(`❌ Transport disconnected: ${error?.message}`);
        },
      };

      this.registerer = new Registerer(this.userAgent, registererOptions);

      this.registerer.stateChange.addListener((state) => {
        console.log(`📞 SIP Registration State Changed: ${state}`);
        this.sipRegistrationState$.next(state);

        switch (state) {
          case RegistererState.Registered:
            this.sipRegistrationState$.next(RegistererState.Registered);
            this.isReconnectingSubject.next(false);
            this.delegate?.onRegistered?.();
            break;
          case RegistererState.Unregistered:
            this.delegate?.onUnregistered?.();
            break;
          case RegistererState.Terminated:
            // Clear calls when registration is terminated
            this.clearAllCalls();
            this.registerer = undefined;
            break;
        }
      });

      await this.userAgent.start();
      console.log('✅ SIP.js User Agent started');
      await this.logService.logInfo(
        `SIP User Agent started for ${this.sip.publicIdentity}\n`,
        LogModule.SIP
      );
      // ✅ Only register if not already registered or registering
      if (
        this.registerer.state === RegistererState.Initial ||
        this.registerer.state === RegistererState.Unregistered
      ) {
        await this.registerer.register();
      } else {
        console.log(
          `⚠️ Registerer already in state: ${this.registerer.state}, skipping registration`
        );
      }

      // Subscribe to presence for the user's own status
      await this.subscribeToPresence();

      const dialogOptions = {
        extraHeaders: [
          'Accept: application/dialog-info+xml',
          'Allow-Events: presence, dialog, message-summary, refer',
        ],
      };
      var subscriber = new Subscriber(
        this.userAgent,
        UserAgent.makeURI(`sips:myRemote@${this.sip.realm}`)!,
        'dialog',
        dialogOptions
      );
      subscriber.delegate = {
        onNotify: (notification: Notification) => {
          // get NOTIFY body
          const body = notification.request.body;
          console.log('COMING FOR NOTIFY', body);
          if (body) {
            this.dialogInfoXmlSubject.next(body);
          }
          notification.accept();
        },
      };

      subscriber.subscribe();

      console.log(`📢 Your SIP Softphone Number: ${this.sip.publicIdentity}`);

      // ✅ Log SIP Registration State
      console.log('📢 SIP Registerer State:', this.registerer.state);

      console.log('✅ Registered with SIP server:', this.sip.server);
      // ✅ Add delegate for incoming calls
      // Set delegate for incoming calls
      this.userAgent.delegate = {
        onInvite: async (invitation: Invitation) => {
          console.log('Incoming call received.');
          console.log(invitation.state);
          await this.handleNewSession(invitation);
        },
      };
    } catch (error: any) {
      await this.logService.logError(
        `❌ SIP Initialization Failed: ${error.message}`,
        LogModule.SIP
      );

      throw new Error(`❌ SIP Initialization Failed: ${error.message}`);
    }
  }

  /**
   * Wraps a new SIP session (incoming or outgoing) into an EveCall,
   * adds it to activeCalls, and sets up a listener for its state changes.
   */
  private async handleNewSession(
    session: Invitation | Inviter,
    newCall?: EveCall
  ) {
    newCall = newCall || new EveCall(session, this.logService);

    // Optionally log the initial state.
    console.log(`Initial state for ${newCall.remoteUser}: ${session.state}`);

    this.updateCallLists(newCall, session, session.state);

    // For incoming invites, play ringtone
    if (
      session instanceof Invitation &&
      session.state === SessionState.Initial
    ) {
      await this.playRingtone();
    }

    const callKey = this.getCallKey(newCall);

    // Remove existing listener if any
    this.removeSessionStateListener(callKey);

    const stateChangeListener = async (state: any) => {
      {
        console.log(`Call with ${newCall?.remoteUser} changed state: ${state}`);

        (async () => {
          await this.logService.logInfo(
            `Call with ${newCall?.remoteUser} changed state: ${state}`,
            LogModule.SIP
          );
        })();

        // **Incoming**: stop ringtone once not in Initial
        if (session instanceof Invitation && state !== SessionState.Initial) {
          this.stopRingtone();
        }

        // **Outgoing**: play/stop ringback on Establishing/Established|Terminated
        if (session instanceof Inviter) {
          if (state === SessionState.Establishing) {
            await this.playRingback();
          } else if (
            state === SessionState.Established ||
            state === SessionState.Terminated
          ) {
            this.stopRingback();
          }
        }

        if (state === SessionState.Established) {
          const sdh =
            session.sessionDescriptionHandler as SessionDescriptionHandler;

          if (sdh?.peerConnection) {
            const remoteStream = new MediaStream();
            sdh.peerConnection.getReceivers().forEach((receiver) => {
              if (receiver.track) {
                remoteStream.addTrack(receiver.track);
              }
            });

            this.remoteStreamSubject.next(remoteStream);

            // 🔊 Assign to <audio> element if available
            if (this.remoteAudioElement) {
              this.remoteAudioElement.srcObject = remoteStream;
              this.remoteAudioElement.play().catch((err) => {
                this.logService.logError(
                  `🔇 Failed to play remote audio: ${err.message}`,
                  LogModule.SIP
                );
                throw new Error(
                  `🔇 Failed to play remote audio: ${err.message}`
                );
              });
            }
          }
        }

        // ✅ NEW: Emit termination event when session terminates
        if (state === SessionState.Terminated) {
          this.callTerminatedSubject.next(newCall!);
          const callKey = this.getCallKey(newCall!);
          // Remove existing listener if any
          this.removeSessionStateListener(callKey);
        }
        // Update the call status for UI updates

        //update session state
        this.updateCallLists(newCall!, session, state);
      }
    };
    // Listen for state changes and update the call's state subject.
    session.stateChange.addListener(stateChangeListener);

    // Store reference for cleanup
    this.sessionStateListeners.set(callKey, () => {
      newCall?.session?.stateChange.removeListener(stateChangeListener);
    });
  }

  async transferCall(call: EveCall, target: string): Promise<void> {
    try {
      await call.transferTo(target, this.sip.realm);
      // this.removeActiveCall(call);
    } catch (error: any) {
      this.logService.logError(
        '❌ Transfer failed: ' + error.message,
        LogModule.SIP
      );
      throw error;
    }
  }

  private updateCallLists(
    newCall: EveCall,
    session: Invitation | Inviter,
    state: SessionState
  ) {
    console.log(`📞 Updating call lists for ${newCall.remoteUser}: ${state}`);

    // Your list-management logic here...
    // For example, for an incoming call:
    if (session instanceof Invitation) {
      if (state === SessionState.Initial) {
        // Remains in the incoming list.
        this.addIncomingCall(newCall);
      } else if (state === SessionState.Establishing) {
        this.removeIncomingCall(newCall);
      } else if (state === SessionState.Established) {
        this.addActiveCall(newCall);
      }
    } else {
      // Outgoing call: add to active list when established.
      if (
        state === SessionState.Established ||
        state === SessionState.Establishing
      ) {
        this.addActiveCall(newCall);
      }

      this.updateActiveCalls(newCall);
    }

    if (state === SessionState.Terminated) {
      this.removeIncomingCall(newCall);
      this.removeActiveCall(newCall);
      console.log(`🗑️ Call terminated and removed: ${newCall.remoteUser}`);
    }
  }

  // Helper methods to manage incoming calls list
  private addIncomingCall(call: EveCall) {
    const current = this.incomingCallsSubject.value;
    // Check for duplicates by comparing session IDs and call details
    const existingCall = current.find(
      (c) =>
        c.session === call.session ||
        (c.remoteUser === call.remoteUser && c.direction === call.direction)
    );

    if (!existingCall) {
      this.incomingCallsSubject.next([...current, call]);
      console.log(
        `✅ Added incoming call: ${call.remoteUser} (${call.direction})`
      );
    } else {
      console.log(
        `⚠️ Duplicate incoming call detected, not adding: ${call.remoteUser}`
      );
    }
  }

  private removeIncomingCall(call: EveCall) {
    const updated = this.incomingCallsSubject.value.filter((c) => c !== call);
    this.incomingCallsSubject.next(updated);
  }

  // Add a new call to the active calls list.
  private addActiveCall(call: EveCall) {
    const current = this.activeCallsSubject.value;
    // Check for duplicates by comparing session IDs instead of object references
    const existingCall = current.find(
      (c) =>
        c.session === call.session ||
        (c.id === call.id && c.direction === call.direction)
    );

    if (!existingCall) {
      this.activeCallsSubject.next([...current, call]);
      console.log(
        `✅ Added active call: ${call.remoteUser} (${call.direction})`
      );
    } else {
      console.log(`⚠️ Duplicate call detected, not adding: ${call.remoteUser}`);
    }
  }

  private removeActiveCall(call: EveCall) {
    const updated = this.activeCallsSubject.value.filter((c) => c !== call);
    this.activeCallsSubject.next(updated);
  }

  // Clear all calls when reinitializing or logging out
  private clearAllCalls() {
    this.sessionStateListeners.forEach((removeListener) => {
      removeListener();
    });
    this.sessionStateListeners.clear();

    const activeCalls = this.activeCallsSubject.value;
    const incomingCalls = this.incomingCallsSubject.value;

    // End any active sessions before clearing
    [...activeCalls, ...incomingCalls].forEach((call) => {
      if (call.session && call.session.state !== SessionState.Terminated) {
        try {
          call
            .endCall()
            .catch((err) =>
              console.warn(
                `⚠️ Failed to end call during cleanup: ${err.message}`
              )
            );
        } catch (err) {
          console.warn(`⚠️ Failed to end call during cleanup:`, err);
        }
      }
    });

    // Clear the lists
    this.activeCallsSubject.next([]);
    this.incomingCallsSubject.next([]);
    this.remoteStreamSubject.next(null);
    this.transferableCall = null;

    console.log(
      `🧹 Cleared ${activeCalls.length} active calls and ${incomingCalls.length} incoming calls`
    );
  }

  // ✅ NEW: Remove session state listener
  private removeSessionStateListener(callKey: string): void {
    const removeListener = this.sessionStateListeners.get(callKey);
    if (removeListener) {
      removeListener();
      this.sessionStateListeners.delete(callKey);
      console.log(`🧹 SIP: Removed session listener for ${callKey}`);
    }
  }

  // ✅ NEW: Generate consistent call key
  private getCallKey(call: EveCall): string {
    return call.id;
  }

  async makeCall(
    remoteDisplayName: string,
    target: string,
    options: InviterOptions | null = null
  ): Promise<EveCall> {
    if (!this.userAgent) {
      this.logService.logError('❌ SIP.js not initialized', LogModule.SIP);
      throw new Error('❌ SIP.js not initialized');
    }

    const sipUrl =
      target.indexOf('@') === -1
        ? `sip:${target}@${this.sip.realm}`
        : `sip:${target}`;
    const targetURI = UserAgent.makeURI(sipUrl);
    if (!targetURI) {
      this.logService.logError('❌ Invalid target URI', LogModule.SIP);
      throw new Error('Invalid target URI');
    }

    console.log(`Sending INVITE to ${target}...`);

    options = Object.assign(
      {
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: this.selectedMicId
              ? { deviceId: { exact: this.selectedMicId } }
              : true,
            video: false,
          },
        },
      },
      options || {}
    );

    // Create an outgoing session.
    const outgoingSession = new Inviter(this.userAgent, targetURI, options);

    // Process the new outgoing session.
    const newCall = new EveCall(outgoingSession, this.logService);
    newCall.remoteUser = remoteDisplayName;
    await this.handleNewSession(outgoingSession, newCall);
    // Send the INVITE.
    try {
      await outgoingSession.invite();
      console.log('Call request sent successfully!');
      return newCall;
    } catch (error: any) {
      this.logService.logError(
        '❌ Call failed: ' + error?.message,
        LogModule.SIP
      );
      throw error;
    }
  }

  async endCall(call: EveCall) {
    console.log(`Ending call with ${call.remoteUser}...`);
    try {
      await call.endCall();
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to end call with ${call.remoteUser}: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  /**
   * Accepts an incoming call.
   * @param call The EveCall instance representing the incoming call.
   */
  public async acceptCall(call: EveCall): Promise<void> {
    try {
      await call.acceptCall();
    } catch (error: any) {
      await this.logService.logError(
        `❌ Failed to accept call with ${call.remoteUser}: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  /**
   * Rejects an incoming call.
   * @param call The EveCall instance representing the incoming call.
   */
  async rejectCall(call: EveCall) {
    try {
      await call.rejectCall();
    } catch (error: any) {
      await this.logService.logError(
        `❌ Failed to reject call with ${call.remoteUser}: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);
      this.recordedChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        console.log('🎤 Recording Stopped.');
      };

      this.mediaRecorder.start();
      console.log('🎤 Recording Started...');
    } catch (error: any) {
      await this.logService.logError(
        `❌ Failed to start recording: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async playRecording() {
    if (this.recordedChunks.length === 0) {
      console.warn('⚠️ No recording available to play.');
      return;
    }

    const audioBlob = new Blob(this.recordedChunks, { type: 'audio/webm' });
    const audioURL = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioURL);
    await audio.play();
    console.log('🔊 Playing recorded audio...');
  }

  stopRecording() {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      console.log('🎤 Stopping recording...');
    }
  }

  enumerateAudioDevices = async (): Promise<{
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  } | null> => {
    try {
      // 🔐 Request permissions first

      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });

      if (status.state === 'denied' || status.state === 'prompt') {
        console.warn('❌ Microphone access denied by user.');
        console.log('🎤 Mic access already granted.');

        var mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        mic.getTracks().forEach((track) => track.stop()); // Stop the track immediately
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(
        (device) => device.kind === 'audioinput'
      );
      const speakers = devices.filter(
        (device) => device.kind === 'audiooutput'
      );

      // this.microphonesSubject.next(microphones);
      // this.speakersSubject.next(speakers);
      return { microphones, speakers };
    } catch (error: any) {
      await this.logService.logError(
        `❌ Failed to get media devices: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  };

  async selectMicrophone(deviceId: string) {
    this.selectedMicrophone.next(deviceId);
    console.log(`🎤 Selected Microphone: ${deviceId}`);
    this.selectedMicId = deviceId;

    try {
      // 🛑 Stop previous stream
      if (this.activeStream) {
        this.activeStream.getTracks().forEach((track) => track.stop());
        console.log('🛑 Stopped previous microphone stream.');
      }

      // 🎤 Get new stream with selected mic
      const constraints: MediaStreamConstraints = {
        audio: { deviceId: { exact: deviceId } },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.activeStream = stream;
      console.log('🎤 New microphone stream activated:', stream);

      // ✅ Get current active calls once
      let activecalls = await firstValueFrom(this.activeCalls$);
      let incomingcalls = await firstValueFrom(this.incomingCalls$);
      const calls = [...activecalls, ...incomingcalls];

      for (const call of calls) {
        const session = call.session;
        const sdh =
          session.sessionDescriptionHandler as SessionDescriptionHandler;

        if (sdh?.peerConnection) {
          const sender = sdh.peerConnection
            .getSenders()
            .find((s) => s.track?.kind === 'audio');

          if (sender) {
            sender.replaceTrack(stream.getAudioTracks()[0]);
            console.log(
              `✅ Mic replaced for ${call.direction} call to ${call.remoteUser}`
            );
          }
        }
      }
    } catch (error: any) {
      await this.logService.logError(
        `❌ Failed to switch microphone: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async selectSpeaker(deviceId: string) {
    try {
      this.selectedSpeaker.next(deviceId);
      console.log(`🔊 Selected Speaker: ${deviceId}`);
      this.selectedSpeakerId = deviceId;

      if (
        this.remoteAudioElement &&
        'setSinkId' in HTMLMediaElement.prototype
      ) {
        await (this.remoteAudioElement as any).setSinkId(deviceId);
        console.log(`🔊 Audio output set to device: ${deviceId}`);
      } else {
        console.warn('⚠️ setSinkId is not supported on this browser.');
        alert(
          '⚠️ Your browser does not support changing audio output devices dynamically. Please set it manually in your OS audio settings.'
        );
      }

      // Update the ringtone audio element.
      if (this.ringtoneAudio && this.ringtoneAudio.setSinkId) {
        await this.ringtoneAudio.setSinkId(deviceId);
        console.log(`Ringtone audio output set to device: ${deviceId}`);
      } else {
        console.warn(
          '⚠️ setSinkId is not supported for the ringtone audio element.'
        );
        alert(
          '⚠️ Your browser does not support changing audio output devices dynamically. Please set it manually in your OS audio settings.'
        );
      }
    } catch (error: any) {
      await this.logService.logError(
        `❌ Failed to set audio output device: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  private async playRingtone() {
    try {
      /* if (!this.userHasInteracted) {
        console.warn('🔇 Cannot play ringtone - no user interaction yet');
        this.logService.logError(
          `🔇 Cannot play ringtone - no user interaction yet`,
          LogModule.SIP
        );
        throw new Error('User interaction required to play ringtone');
      }*/

      this.ringtoneAudio.currentTime = 0;
      await this.ringtoneAudio.play();
    } catch (err) {
      await this.logService.logError(
        `❌ Failed to play ringtone: ${
          err instanceof Error ? err.message : err
        }`,
        LogModule.SIP
      );
      throw err;
    }
  }

  private stopRingtone() {
    this.ringtoneAudio.pause();
    this.ringtoneAudio.currentTime = 0;
  }

  private async playRingback() {
    if (!this.userHasInteracted) return;
    this.ringbackAudio.currentTime = 0;
    this.ringbackAudio.play().catch((err) => {
      this.logService.logError(
        `❌ Failed to play ringback: ${
          err instanceof Error ? err.message : err
        }`,
        LogModule.SIP
      );
      throw new Error(
        `❌ Failed to play ringback: ${
          err instanceof Error ? err.message : err
        }`
      );
    });
  }

  private stopRingback() {
    this.ringbackAudio.pause();
    this.ringbackAudio.currentTime = 0;
  }

  public async register() {
    try {
      this.isReconnectingSubject.next(true);
      if (!this.userAgent) {
        console.warn('⚠️ userAgent not found — initializing SIP first...');
        await this.initializeSIP(); // will register as part of init
        this.reconnectAttempts = 0;
        return;
      }

      // 🛠 Transport might be closed, try to restart it
      if (this.userAgent.transport?.isConnected() === false) {
        console.log('🔌 Transport not connected. Restarting user agent...');
        // await this.userAgent.stop();
        await this.userAgent.reconnect();
      }

      if (!this.registerer) {
        console.log('🛠 Creating registerer...');
        this.registerer = new Registerer(this.userAgent, registererOptions);
        this.registerer.stateChange.addListener(async (state) => {
          console.log(`📞 SIP Registration State Changed: ${state}`);
          await this.logService.logInfo(
            `📞 SIP Registration State Changed: ${state}`,
            LogModule.SIP
          );
          this.sipRegistrationState$.next(state);

          switch (state) {
            case RegistererState.Registered:
              this.delegate?.onRegistered?.();
              this.isReconnectingSubject.next(false);
              this.reconnectAttempts = 0;
              break;
            case RegistererState.Unregistered:
              this.scheduleReconnection();
              this.delegate?.onUnregistered?.();
              break;
            case RegistererState.Terminated:
              this.registerer = undefined;
              this.scheduleReconnection();
              break;
          }
        });
      }

      await this.registerer.register();
      console.log('✅ SIP manually registered.');
      this.isReconnectingSubject.next(false);
      this.reconnectAttempts = 0;
    } catch (err: any) {
      this.logService.logError(
        `❌ Manual registration failed: ${
          err instanceof Error ? err.message : err
        }`,
        LogModule.SIP
      );
      this.scheduleReconnection();
    }
  }

  private scheduleReconnection() {
    this.reconnectAttempts++;
    this.isReconnectingSubject.next(true);

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      this.isReconnectingSubject.next(false);
      this.logService.logError(
        `❌ Max SIP reconnect attempts reached.`,
        LogModule.SIP
      );
      throw new Error(`❌ Max SIP reconnect attempts reached.`);
    }

    const delay = Math.min(10000, 1000 * Math.pow(2, this.reconnectAttempts)); // max 10s
    console.warn(
      `🔁 Reconnecting SIP in ${delay / 1000}s (attempt ${
        this.reconnectAttempts
      })...`
    );

    setTimeout(async () => {
      await this.register();
    }, delay);
  }

  private async autoReconnectIfNeeded() {
    if (!navigator.onLine) return;

    const currentState = this.sipRegistrationState$.value;
    const shouldReconnect =
      !this.userAgent ||
      !this.registerer ||
      currentState !== RegistererState.Registered;

    if (shouldReconnect) {
      console.log('🔄 Attempting automatic SIP reconnect...');
      try {
        await this.register(); // smart version you now have
      } catch (err) {
        this.logService.logError(
          `❌ Auto SIP reconnect failed: ${
            err instanceof Error ? err.message : err
          }`,
          LogModule.SIP
        );
        throw new Error(
          `❌ Auto SIP reconnect failed: ${
            err instanceof Error ? err.message : err
          }`
        );
      }
    } else {
      console.log('✅ SIP is already registered — no action needed');
    }
  }

  getSelectedMicrophone(): string | null {
    return this.selectedMicId;
  }

  getSelectedSpeaker(): string | null {
    return this.selectedSpeakerId;
  }

  getActiveCalls(): EveCall[] {
    return this.activeCallsSubject.value;
  }
  setActiveCalls(calls: EveCall[]) {
    this.activeCallsSubject.next(calls);
  }

  // Get the cached transferable call for efficient transfer operations
  getTransferableCall(): EveCall | null {
    return this.transferableCall;
  }

  // Public method to clear all calls (useful for logout/cleanup)
  public clearActiveCalls(): void {
    this.clearAllCalls();
  }

  // ✅ ADD: Get the current active microphone stream
  getActiveStream(): MediaStream | null {
    return this.activeStream;
  }

  // ✅ ADD: Get microphone stream (create new one if needed)
  async getMicrophoneStream(): Promise<MediaStream | null> {
    try {
      // Return existing stream if available and active
      if (this.activeStream && this.activeStream.active) {
        return this.activeStream;
      }

      // Create new stream with selected microphone
      const constraints: MediaStreamConstraints = {
        audio: this.selectedMicId
          ? {
              deviceId: { exact: this.selectedMicId },
              echoCancellation: true,
              noiseSuppression: true,
            }
          : { echoCancellation: true, noiseSuppression: true },
        video: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.activeStream = stream;

      console.log('🎤 Created new microphone stream:', stream);
      return stream;
    } catch (error) {
      console.error('Failed to get microphone stream:', error);
      await this.logService.logError(
        `❌ Failed to get microphone stream: ${
          error instanceof Error ? error.message : error
        }`,
        LogModule.SIP
      );
      return null;
    }
  }

  // ✅ ADD: Restore proper call audio (used by conference service)
  // ✅ ADD: Restore proper call audio (used by conference service)
  async restoreCallAudio(call: EveCall): Promise<void> {
    try {
      console.log(`🔄 SipService restoring audio for ${call.remoteUser}`);

      const sdh = (call.session as any)?.sessionDescriptionHandler as
        | SessionDescriptionHandler
        | undefined;
      const pc: RTCPeerConnection | undefined = sdh?.peerConnection;

      if (!pc) {
        throw new Error('No peer connection available');
      }

      // Get the audio sender
      const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
      if (!sender) {
        throw new Error('No audio sender found');
      }

      // Get the original microphone stream
      const micStream = await this.getMicrophoneStream();
      if (!micStream || micStream.getAudioTracks().length === 0) {
        throw new Error('No microphone stream available');
      }

      // Replace the sender track with original microphone
      const micTrack = micStream.getAudioTracks()[0];
      await sender.replaceTrack(micTrack);

      // Ensure proper transceiver direction
      pc.getTransceivers().forEach((transceiver) => {
        if (transceiver.sender === sender) {
          transceiver.direction = 'sendrecv';
        }
      });

      console.log(`✅ SipService restored audio for ${call.remoteUser}`);
    } catch (error) {
      console.error(
        `Failed to restore call audio for ${call.remoteUser}:`,
        error
      );
      throw error;
    }
  }

  public updateActiveCalls(call: EveCall): void {
    const currentCalls = this.activeCallsSubject.value;
    const index = currentCalls.findIndex((c) => c.id === call.id);
    if (index !== -1) {
      currentCalls[index] = call;
      this.activeCallsSubject.next([...currentCalls]);
      console.log(`✅ Updated active call: ${call.remoteUser}`);
    }
  }

  private async subscribeToPresence(): Promise<void> {
    if (!this.userAgent) {
      throw new Error('User agent not initialized');
    }

    const presenceOptions = {
      extraHeaders: [
        'Accept: application/pidf+xml, application/xpidf+xml',
        'Allow-Events: presence, dialog, message-summary, refer',
      ],
      expires: 600, // 10 minutes
    };

    // Subscribe to own presence
    const targetUri = UserAgent.makeURI(
      `sips:${this.sip.privateIdentity}@${this.sip.realm}`
    );
    if (!targetUri) {
      throw new Error('Failed to create presence target URI');
    }

    this.presenceSubscriber = new Subscriber(
      this.userAgent,
      targetUri,
      'presence',
      presenceOptions
    );

    this.presenceSubscriber.delegate = {
      onNotify: (notification: Notification) => {
        const body = notification.request.body;
        console.log('📢 PRESENCE NOTIFY received:', body);

        if (body) {
          this.parsePresenceXml(body);
        }

        notification.accept();
      },
    };

    await this.presenceSubscriber.subscribe();
    console.log('✅ Subscribed to presence events');
    await this.logService.logInfo(
      `Subscribed to presence for ${this.sip.publicIdentity}\n`,
      LogModule.SIP
    );
  }

  private parsePresenceXml(xmlString: string): void {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

      // Extract activity status
      const activityElement = xmlDoc.querySelector(
        'telepo\\:activity, activity'
      );
      const activityText =
        activityElement?.textContent?.toLowerCase() || 'outofoffice';

      // Map XML values to UserPresenceState enum
      const activity = this.mapXmlActivityToEnum(activityText);

      // // Extract note (if present)
      // const noteElement = xmlDoc.querySelector('dm\\:note, note');
      // const note = noteElement?.textContent || undefined;

      // // Extract role
      // const roleElement = xmlDoc.querySelector('telepo\\:role, role');
      // const role = roleElement?.textContent || undefined;

      // // Extract preferred URI
      // const preferredUriElement = xmlDoc.querySelector(
      //   'telepo\\:preferred-uri, preferred-uri'
      // );
      // const preferredUri = preferredUriElement?.textContent || undefined;

      // // Extract contact status
      // const contactStatusElement = xmlDoc.querySelector(
      //   'telepo\\:contactStatus, contactStatus'
      // );
      // const contactStatus = contactStatusElement?.textContent || undefined;

      // const presence: UserPresence = {
      //   activity,
      //   note,
      //   role,
      //   preferredUri,
      //   contactStatus,
      // };

      // console.log('📊 Parsed presence:', presence);
      this.userPresenceSubject.next(activity);
    } catch (error) {
      console.error('Failed to parse presence XML:', error);
      this.logService.logError(
        `Failed to parse presence XML: ${
          error instanceof Error ? error.message : error
        }`,
        LogModule.SIP
      );
    }
  }

  public setUserPresenceState(state: UserPresenceState) {
    this.userPresenceSubject.next(state);
  }

  /**
   * Maps XML activity values (lowercase) to UserPresenceState enum values
   */
  private mapXmlActivityToEnum(xmlActivity: string): UserPresenceState {
    switch (xmlActivity.toLowerCase()) {
      case 'available':
        return UserPresenceState.Available;
      case 'busy':
        return UserPresenceState.Busy;
      case 'vacation':
        return UserPresenceState.Holiday;
      case 'lunch':
        return UserPresenceState.Lunch;
      case 'meeting':
        return UserPresenceState.Meeting;
      case 'outofoffice':
      case 'out of office':
        return UserPresenceState.OutOfOffice;
      default:
        return UserPresenceState.OutOfOffice;
    }
  }
}
